import React from "react";
import { Box, Slider } from "@material-ui/core";
import RgbaColorPickerPopover from "./RgbaColorPickerPopover";
import HexColorPickerPopover from "./HexColorPickerPopover";
import {
	TEXT_EFFECT_OPTIONS,
	getTextEffectPreviewStyle,
	normalizeTextEffectShadow,
	normalizeTextEffectGlow,
	normalizeTextEffectGlitch,
	DEFAULT_TEXT_EFFECT_SHADOW,
	DEFAULT_TEXT_EFFECT_GLOW,
	DEFAULT_TEXT_EFFECT_GLITCH,
} from "./textEffectStyles";
import { DraftIntTextField } from "./escapeRoomEditorFloatingWidgets";

/** effectStyle: { textEffect, textEffectShadow, textEffectGlow, textEffectGlitch, color? } */
export default function TextEffectPicker({ effectStyle = {}, onEffectStyleChange, fallbackColor, embedded = false }) {
	const style = effectStyle || {};
	const effect = style.textEffect || "none";
	const shadow = normalizeTextEffectShadow(style.textEffectShadow);
	const glow = normalizeTextEffectGlow(style.textEffectGlow, fallbackColor || style.color);
	const glitch = normalizeTextEffectGlitch(style.textEffectGlitch);

	const patchStyle = (partial) => onEffectStyleChange({ ...style, ...partial });

	const patchShadow = (partial) =>
		patchStyle({
			textEffectShadow: { ...shadow, ...partial },
		});

	const patchGlow = (partial) =>
		patchStyle({
			textEffectGlow: { ...glow, ...partial },
		});

	const patchGlitch = (partial) =>
		patchStyle({
			textEffectGlitch: { ...glitch, ...partial },
		});

	const inner = (
		<>
			<Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" style={{ gap: 6, width: "100%" }}>
				{TEXT_EFFECT_OPTIONS.map((opt) => {
					const selected = effect === opt.id;
					return (
						<Box
							key={opt.id}
							role="button"
							tabIndex={0}
							onClick={() => patchStyle({ textEffect: opt.id })}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									patchStyle({ textEffect: opt.id });
								}
							}}
							style={{
								cursor: "pointer",
								borderRadius: 8,
								padding: "6px 4px",
								textAlign: "center",
								background: "#f8fafc",
								border: selected ? "2px solid #7c3aed" : "1px solid #e2e8f0",
								minWidth: 0,
								boxSizing: "border-box",
							}}
						>
							<Box
								style={{
									fontSize: 24,
									fontWeight: 800,
									lineHeight: 1,
									minHeight: 30,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									...getTextEffectPreviewStyle(opt.id, style),
								}}
							>
								A
							</Box>
							<Box fontSize={9} color={selected ? "#7c3aed" : "#64748b"} fontWeight={selected ? 600 : 500} mt={0.25}>
								{opt.label}
							</Box>
						</Box>
					);
				})}
			</Box>

			{effect === "shadow" && (
				<Box mt={1.25} pt={1.25} style={{ borderTop: "1px solid #e2e8f0" }}>
					<Box display="flex" style={{ gap: 6, marginBottom: 8 }}>
						<DraftIntTextField label="X" min={-40} max={40} allowNegative value={shadow.offsetX} onCommit={(n) => patchShadow({ offsetX: n })} style={{ flex: 1, minWidth: 0 }} variant="outlined" />
						<DraftIntTextField label="Y" min={-40} max={40} allowNegative value={shadow.offsetY} onCommit={(n) => patchShadow({ offsetY: n })} style={{ flex: 1, minWidth: 0 }} variant="outlined" />
					</Box>
					<Box mb={0.75}>
						<Box display="flex" justifyContent="space-between" mb={0.25}>
							<Box fontSize={10} color="#64748b">Difuminado</Box>
							<Box fontSize={10} color="#64748b">{shadow.blur}px</Box>
						</Box>
						<Slider value={shadow.blur} min={0} max={32} onChange={(_, v) => patchShadow({ blur: v })} color="primary" />
					</Box>
					<RgbaColorPickerPopover label="Color sombra" value={shadow.color} onChange={(c) => patchShadow({ color: c || DEFAULT_TEXT_EFFECT_SHADOW.color })} />
				</Box>
			)}

			{effect === "glow" && (
				<Box mt={1.25} pt={1.25} style={{ borderTop: "1px solid #e2e8f0" }}>
					<Box mb={0.75}>
						<Box display="flex" justifyContent="space-between" mb={0.25}>
							<Box fontSize={10} color="#64748b">Intensidad</Box>
							<Box fontSize={10} color="#64748b">{glow.intensity}%</Box>
						</Box>
						<Slider value={glow.intensity} min={0} max={100} onChange={(_, v) => patchGlow({ intensity: v })} color="primary" />
					</Box>
					<Box style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
						<HexColorPickerPopover label="Color resplandor" value={glow.color} onChange={(c) => patchGlow({ color: c || DEFAULT_TEXT_EFFECT_GLOW.color })} />
					</Box>
				</Box>
			)}

			{effect === "glitch" && (
				<Box mt={1.25} pt={1.25} style={{ borderTop: "1px solid #e2e8f0" }}>
					<Box display="flex" flexDirection="column" style={{ gap: 10, width: "100%", maxWidth: "100%" }}>
						<Box style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
							<HexColorPickerPopover label="Color A" value={glitch.colorA} onChange={(c) => patchGlitch({ colorA: c || DEFAULT_TEXT_EFFECT_GLITCH.colorA })} />
						</Box>
						<Box style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
							<HexColorPickerPopover label="Color B" value={glitch.colorB} onChange={(c) => patchGlitch({ colorB: c || DEFAULT_TEXT_EFFECT_GLITCH.colorB })} />
						</Box>
					</Box>
					<Box mb={0.75} mt={1}>
						<Box display="flex" justifyContent="space-between" mb={0.25}>
							<Box fontSize={10} color="#64748b">Intensidad</Box>
							<Box fontSize={10} color="#64748b">{glitch.intensity}%</Box>
						</Box>
						<Slider value={glitch.intensity} min={0} max={100} onChange={(_, v) => patchGlitch({ intensity: v })} color="primary" />
					</Box>
					<Box>
						<Box display="flex" justifyContent="space-between" mb={0.25}>
							<Box fontSize={10} color="#64748b">Velocidad</Box>
							<Box fontSize={10} color="#64748b">{glitch.speed}%</Box>
						</Box>
						<Slider value={glitch.speed} min={0} max={100} onChange={(_, v) => patchGlitch({ speed: v })} color="primary" />
					</Box>
				</Box>
			)}
		</>
	);

	if (embedded) {
		return <Box style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>{inner}</Box>;
	}

	return (
		<Box
			p={1.5}
			borderRadius={10}
			style={{
				background: "#fff",
				border: "1px solid #e2e8f0",
				width: "100%",
				maxWidth: "100%",
				boxSizing: "border-box",
				overflow: "hidden",
			}}
		>
			<Box fontSize={12} fontWeight={600} color="#475569" mb={1}>
				Efecto visual
			</Box>
			{inner}
		</Box>
	);
}
